/**
 * MESSAGING SCREEN — OpenPhone Proxy Communication
 *
 * Chat with buyer, seller, or support agent through the Commonplace proxy.
 * Driver messages in-app → backend → OpenPhone API → customer receives SMS.
 * Customer replies via SMS → OpenPhone webhook → backend → polled here.
 *
 * Context-aware: before pickup = seller-focused, after pickup = buyer-focused.
 * Supports quick template messages and proxy calling.
 */
import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import {
  View, Text, StyleSheet, FlatList, TextInput,
  TouchableOpacity, KeyboardAvoidingView, Platform,
  Alert, ScrollView,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Ionicons } from '@expo/vector-icons';
import { spacing, radii, RD } from '../../theme/colors';
import { proxyMessaging, RecipientType, ProxyMessage, QUICK_MESSAGES } from '../../services/proxy-messaging';

// Local cache key — keeps the thread visible immediately on cold reopen,
// then the backend poll refreshes it. Bump _v1 if the ProxyMessage shape changes.
const cacheKey = (jobId: string) => `cp_msgs_v1_${jobId}`;

// Persist all non-system messages (including optimistic/pending ones) so a thread
// survives the driver leaving and reopening the app. Called synchronously the
// instant a message is added — not just on the next poll — so a send-then-close
// can never drop a message.
const persistMessages = (jobId: string, msgs: ProxyMessage[]) => {
  const toCache = msgs.filter(m => m.senderType !== 'SYSTEM');
  AsyncStorage.setItem(cacheKey(jobId), JSON.stringify(toCache)).catch(() => {});
};

const PICKUP_STATUSES = ['SCHEDULED', 'STARTED', 'EN_ROUTE_TO_SELLER', 'ARRIVED_PICKUP', 'PICKUP_CHECKLIST_COMPLETE', 'SELLER_SIGNATURE_COMPLETE'];

export function MessagingScreen({ route, navigation }: any) {
  const { jobId, job, initialRecipient, onBehalfOfDriverId, onBehalfOfDriverName } = route.params;
  const jobStatus = job?.status || 'ACCEPTED';
  const isPickupPhase = PICKUP_STATUSES.includes(jobStatus);

  // 🚨 A store-to-sell or split long-haul pickup HAS NO BUYER. The "buyer" on an STS job is
  // our own storage account, with no phone and no thread, so its tab can only ever be empty.
  // Opening on it (which happens whenever the job is not yet in a pickup status) showed the
  // driver "No messages yet" while the seller was texting him on the other tab. One party,
  // one tab. Matches the ActiveJobScreen test - by leg OR by type, plus storage/STS.
  const isPickupOnly = !!(
    job?.legMode === 'PICKUP_ONLY' ||
    job?.jobType === 'PICKUP_ONLY' ||
    job?.categorySlug === 'storage' ||
    String(job?.wooOrderId || '').startsWith('STS-')
  );
  const TABS: RecipientType[] = isPickupOnly ? ['SELLER'] : ['SELLER', 'BUYER'];

  // Resolve the customer's real phone number based on recipient type.
  // This is used internally by the proxy service and never shown in the UI.
  const getCustomerPhone = (r: RecipientType): string => {
    if (r === 'SELLER') return job?.sellerPhone || '';
    if (r === 'BUYER') return job?.buyerPhone || '';
    return ''; // AGENT doesn't use phone
  };

  // Seed with a system welcome message so the thread doesn't look broken
  const [messages, setMessages]     = useState<ProxyMessage[]>([
    {
      id: 'sys-welcome',
      jobId,
      messageText: 'Messages are sent through Commonplace. The customer will receive them as a normal text message.',
      senderType: 'SYSTEM',
      recipientType: 'BUYER',
      isInbound: false,
      timestamp: new Date(Date.now() - 60000).toISOString(),
    },
  ]);
  const [text, setText]             = useState('');
  // Per-order thread is Quo (Seller/Buyer) ONLY — never the support/agent
  // channel. Any stray AGENT request falls back to the phase-appropriate party.
  const [recipient, setRecipient]   = useState<RecipientType>(
    isPickupOnly
      ? 'SELLER'
      : (initialRecipient && initialRecipient !== 'AGENT')
        ? initialRecipient
        : (isPickupPhase ? 'SELLER' : 'BUYER')
  );
  // Did the driver (or the caller) actually pick this tab? If not, the status guess above is
  // only a guess, and we correct it below once we know which thread has a conversation.
  const [tabChosen, setTabChosen] = useState<boolean>(!!initialRecipient || isPickupOnly);
  // A failed poll must NEVER read as "no messages" - that is the whole bug. Both this screen
  // and the backend used to turn every error into an empty list, so a driver with a live
  // conversation saw an empty thread and no reason why.
  const [loadFailed, setLoadFailed] = useState(false);
  const [showQuick, setShowQuick]   = useState(false);
  const [sending, setSending]       = useState(false);
  const listRef = useRef<FlatList>(null);
  // Guards the post-send `setTimeout(loadMessages, 600)` from firing after unmount
  // (a state update on an unmounted component + a wasted fetch).
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);
  // Per-thread "last seen" timestamp (ms) so the unread badge means UNREAD, not
  // total-inbound-forever. Persisted so it survives reopen.
  const [lastSeen, setLastSeen] = useState<Record<string, number>>({ BUYER: 0, SELLER: 0, AGENT: 0 });

  // Merge backend fetch result into local state. CRITICAL: only replace messages
  // for the CURRENT recipient thread — keep prev messages for other threads
  // (BUYER/SELLER/AGENT) so switching tabs doesn't wipe them. Also caches
  // optimistic sends so they survive a cold reopen before the backend confirms.
  const mergeIntoState = useCallback((data: ProxyMessage[]) => {
    // An empty result is almost always a failed/no-op poll — NEVER let it wipe the
    // thread (the old code dropped confirmed messages expecting them back from the
    // server, so one network blip blanked the conversation).
    if (!Array.isArray(data) || data.length === 0) return;
    setMessages(prev => {
      // Union by stable id. Server messages are authoritative for their id; we keep
      // EVERY message we've ever seen (all threads), so a poll that doesn't include a
      // message can't remove it. This is the core reliability fix.
      const byId = new Map<string, ProxyMessage>();
      for (const m of prev) byId.set(m.id, m);
      for (const m of data) byId.set(m.id, m);
      let all = Array.from(byId.values());

      // Collapse optimistic sends ('m-…') the server has now confirmed, so a sent message
      // doesn't show twice / sit stuck on "Sending". CRITICAL: the backend AI-POLISHES driver
      // messages, so the optimistic bubble's text (what the driver TYPED) won't equal the
      // server's messageText (the rewritten, sent version). We therefore key each server
      // message by BOTH its messageText AND its originalMessageText (= the driver's typed text),
      // so the optimistic — keyed by the typed text — still finds its match and collapses.
      const serverKey = new Map<string, number>();
      for (const m of all) {
        if (m.id.startsWith('m-')) continue;
        const ts = new Date(m.timestamp).getTime();
        const pref = `${m.senderType}:${m.recipientType}:`;
        serverKey.set(pref + (m.messageText || '').trim(), ts);
        if (m.originalMessageText) serverKey.set(pref + m.originalMessageText.trim(), ts);
      }
      all = all.filter(m => {
        if (!m.id.startsWith('m-')) return true;
        const t = serverKey.get(`${m.senderType}:${m.recipientType}:${(m.messageText || '').trim()}`);
        return !(t != null && Math.abs(new Date(m.timestamp).getTime() - t) < 120_000);
      });

      all.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
      persistMessages(jobId, all);
      return all;
    });
  }, [jobId]);

  // Load messages. Per-order threads are Seller + Buyer — poll BOTH every cycle so the
  // inactive tab's messages and unread badge stay live (a driver on Seller still receives
  // Buyer replies in real time, and vice-versa). AGENT is the separate support channel.
  const loadMessages = useCallback(() => {
    if (recipient === 'AGENT') {
      proxyMessaging.getAgentThread(jobId)
        .then(d => { setLoadFailed(false); mergeIntoState(d); })
        .catch(() => setLoadFailed(true));
      return;
    }
    // A pickup-only job has no buyer thread to poll - asking for one is a guaranteed empty
    // round trip every 5 seconds.
    const wanted: RecipientType[] = isPickupOnly ? ['SELLER'] : ['SELLER', 'BUYER'];
    Promise.allSettled(
      wanted.map(r => proxyMessaging.getThread(jobId, r, r === 'SELLER' ? job?.sellerPhone : job?.buyerPhone)
        .then(mergeIntoState)),
    ).then(results => {
      // Only a poll where EVERY thread failed is a real failure - one empty thread is normal.
      setLoadFailed(results.every(r => r.status === 'rejected'));
    });
  }, [jobId, recipient, isPickupOnly, mergeIntoState, job?.sellerPhone, job?.buyerPhone]);

  // Hydrate from local cache on mount so the driver never opens to a blank
  // thread. Runs once per jobId — the polling effect below then refreshes.
  useEffect(() => {
    let cancelled = false;
    AsyncStorage.getItem(cacheKey(jobId)).then(raw => {
      if (cancelled || !raw) return;
      try {
        const cached = JSON.parse(raw) as ProxyMessage[];
        if (Array.isArray(cached) && cached.length > 0) {
          setMessages(prev => {
            const systemMsgs = prev.filter(m => m.senderType === 'SYSTEM');
            const merged = [...systemMsgs, ...cached];
            const seen = new Set<string>();
            return merged
              .filter(m => { if (seen.has(m.id)) return false; seen.add(m.id); return true; })
              .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
          });
        }
      } catch { /* corrupt cache — ignore */ }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [jobId]);

  useEffect(() => {
    loadMessages();
    const interval = setInterval(loadMessages, 5000);
    return () => clearInterval(interval);
  }, [loadMessages]);

  // Filter messages by current recipient thread
  const threadMessages = useMemo(() => {
    return messages.filter(m =>
      m.recipientType === recipient ||
      (m.senderType === recipient && (m.recipientType as string) === 'DRIVER') ||
      ((m.senderType as string) === 'DRIVER' && m.recipientType === recipient)
    );
  }, [messages, recipient]);

  // The opening tab is a GUESS off the job status until the driver picks one. If that guess
  // lands on a thread with nothing in it while the other thread has a live conversation,
  // move: an empty tab in front of a driver who is being texted right now is the failure
  // this screen exists to avoid.
  useEffect(() => {
    if (tabChosen || recipient === 'AGENT') return;
    const hasMessages = (r: RecipientType) => messages.some(m =>
      m.senderType !== 'SYSTEM' && (m.recipientType === r || (m.senderType as string) === r));
    const other: RecipientType = recipient === 'SELLER' ? 'BUYER' : 'SELLER';
    if (!hasMessages(recipient) && hasMessages(other)) setRecipient(other);
  }, [messages, recipient, tabChosen]);

  // Load persisted last-seen on mount.
  useEffect(() => {
    AsyncStorage.getItem(`cp_lastseen_${jobId}`).then(raw => {
      if (raw) { try { setLastSeen(s => ({ ...s, ...JSON.parse(raw) })); } catch { /* ignore */ } }
    }).catch(() => {});
  }, [jobId]);

  // Viewing a thread marks it seen up to now — so its badge clears and only NEWER
  // inbound (on this or the other tab) counts as unread.
  useEffect(() => {
    setLastSeen(prev => {
      const next = { ...prev, [recipient]: Date.now() };
      AsyncStorage.setItem(`cp_lastseen_${jobId}`, JSON.stringify(next)).catch(() => {});
      return next;
    });
  }, [recipient, threadMessages.length, jobId]);

  // Unread counts per thread — inbound newer than the last time the driver viewed it.
  const unreadCounts = useMemo(() => {
    const counts: Record<string, number> = { BUYER: 0, SELLER: 0, AGENT: 0 };
    messages.forEach(m => {
      if (m.isInbound && m.senderType !== 'SYSTEM') {
        const thread = m.senderType as string;
        if (thread in counts && new Date(m.timestamp).getTime() > (lastSeen[thread] || 0)) counts[thread]++;
      }
    });
    return counts;
  }, [messages, lastSeen]);

  const send = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    const optimistic: ProxyMessage = {
      id: `m-${Date.now()}`,
      jobId,
      messageText: text.trim(),
      isInbound: false,
      recipientType: recipient,
      senderType: 'DRIVER',
      timestamp: new Date().toISOString(),
      status: 'PENDING',
    };
    setMessages(prev => { const next = [...prev, optimistic]; persistMessages(jobId, next); return next; });
    setText('');
    setShowQuick(false);

    try {
      let server: ProxyMessage | undefined;
      if (recipient === 'AGENT') {
        server = await proxyMessaging.contactAgent(jobId, optimistic.messageText);
      } else {
        const phone = getCustomerPhone(recipient);
        server = await proxyMessaging.sendMessage(jobId, recipient, optimistic.messageText, phone, onBehalfOfDriverId);
      }
      // Reconcile by the REAL server id immediately, so the bubble flips to Sent and can
      // never get stuck on "Sending" or duplicate — the old text-based twin-match broke
      // whenever the stored text differed (e.g. "&" → "&amp;").
      if (server && !server.id.startsWith('m-')) {
        setMessages(prev => { const next = prev.map(m => m.id === optimistic.id ? { ...server!, status: server!.status || 'SENT' } : m); persistMessages(jobId, next); return next; });
      }
      // Reload to pick up delivery status + any interleaved messages.
      setTimeout(() => { if (mountedRef.current) loadMessages(); }, 600);
    } catch (err: any) {
      // Don't delete the message (that made it "disappear on submit" and lose the driver's
      // text). Keep it, marked FAILED, so the driver sees it didn't send and can resend.
      setMessages(prev => { const next = prev.map(m => m.id === optimistic.id ? { ...m, status: 'FAILED' as const } : m); persistMessages(jobId, next); return next; });
      // Re-fill the composer with the text so a one-tap resend is possible.
      setText(t => t || optimistic.messageText);
      Alert.alert('Message not sent', err?.response?.data?.error || err?.message || 'Could not send — check your connection and try again. Your message was kept.');
    }
    setSending(false);
  };

  const sendQuickMessage = async (templateKey: string) => {
    setSending(true);
    const templates: Record<string, string> = {
      'on-my-way': "I'm on my way! I'll be there shortly.",
      'arrived': "I've arrived. I'm outside.",
      'running-late': "I'm running a few minutes behind. I'll be there soon.",
      'need-access': "Could you provide access instructions for the pickup/delivery location?",
      'loading-complete': "Loading is complete. I'm heading to the delivery location now.",
      'eta-update': "Just a quick update. I should be there within the next 15 to 20 minutes.",
    };
    const messageText = templates[templateKey] || templateKey;
    const optimistic: ProxyMessage = {
      id: `m-${Date.now()}`,
      jobId,
      messageText,
      isInbound: false,
      recipientType: recipient,
      senderType: 'DRIVER',
      timestamp: new Date().toISOString(),
      status: 'PENDING',
    };
    setMessages(prev => { const next = [...prev, optimistic]; persistMessages(jobId, next); return next; });
    setShowQuick(false);
    try {
      const phone = getCustomerPhone(recipient);
      await proxyMessaging.sendQuickMessage(jobId, recipient, templateKey, phone);
      setTimeout(() => { if (mountedRef.current) loadMessages(); }, 600);
    } catch (err: any) {
      // Mark FAILED + alert (was a silent empty catch → message stuck on "Sending").
      setMessages(prev => { const next = prev.map(m => m.id === optimistic.id ? { ...m, status: 'FAILED' as const } : m); persistMessages(jobId, next); return next; });
      Alert.alert('Message not sent', err?.response?.data?.error || err?.message || 'Could not send — please try again.');
    }
    setSending(false);
  };

  const recipientName = recipient === 'BUYER'
    ? (job?.buyerName || 'Buyer')
    : recipient === 'SELLER'
      ? (job?.sellerName || 'Seller')
      : 'Support';

  const headerSub = recipient === 'SELLER'
    ? 'Pickup contact · via Commonplace'
    : recipient === 'BUYER'
      ? 'Delivery contact · via Commonplace'
      : 'via Commonplace';

  return (
    <KeyboardAvoidingView style={styles.container} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backBtn} hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }} activeOpacity={0.7}>
          <Ionicons name="chevron-back" size={24} color={RD.text} />
        </TouchableOpacity>
        <View style={styles.headerCenter}>
          <Text style={styles.headerTitle}>{recipientName}</Text>
          <Text style={styles.headerSub}>{headerSub}</Text>
        </View>
      </View>

      {/* Recipient Tabs — segmented control.
          Per-order threads are Seller/Buyer (Quo/OpenPhone) ONLY. Support lives in
          its own inbox (the Support FAB → SupportInbox) so the agent channel and
          the per-order conversations never bleed into each other. */}
      <View style={styles.tabRow}>
        {TABS.map(r => {
          const isActive = recipient === r;
          const icon = r === 'SELLER' ? 'storefront' : 'person';
          const label = r === 'SELLER' ? 'Seller' : 'Buyer';
          const count = unreadCounts[r] || 0;
          return (
            <TouchableOpacity
              key={r}
              style={[styles.tab, isActive && styles.tabActive]}
              onPress={() => { setTabChosen(true); setRecipient(r); }}
            >
              <Ionicons name={icon} size={15} color={isActive ? '#fff' : RD.g40} />
              <Text style={[styles.tabText, isActive && styles.tabTextActive]}>{label}</Text>
              {count > 0 && !isActive && (
                <View style={styles.unreadDot}>
                  <Text style={styles.unreadText}>{count}</Text>
                </View>
              )}
            </TouchableOpacity>
          );
        })}
      </View>

      {/* Privacy Notice */}
      <View style={styles.proxyNotice}>
        <Ionicons name="shield-checkmark" size={13} color={RD.money} />
        <Text style={styles.proxyText}>
          Sent via Commonplace — numbers stay private.
        </Text>
      </View>

      {/* Messages */}
      <FlatList
        ref={listRef}
        data={threadMessages}
        keyExtractor={i => i.id}
        contentContainerStyle={styles.list}
        onContentSizeChange={() => listRef.current?.scrollToEnd({ animated: true })}
        ListEmptyComponent={
          loadFailed ? (
            // NOT "no messages" - we do not know. Say so, and give a way to try again.
            <View style={styles.emptyState}>
              <Ionicons name="cloud-offline-outline" size={40} color={RD.g30} />
              <Text style={styles.emptyText}>Couldn't load messages</Text>
              <Text style={styles.emptySub}>There may be messages waiting. Check your connection.</Text>
              <TouchableOpacity style={styles.retryBtn} onPress={loadMessages} activeOpacity={0.85}>
                <Ionicons name="refresh" size={15} color="#fff" />
                <Text style={styles.retryText}>Retry</Text>
              </TouchableOpacity>
            </View>
          ) : (
            <View style={styles.emptyState}>
              <Ionicons name="chatbubbles-outline" size={40} color={RD.g30} />
              <Text style={styles.emptyText}>No messages yet</Text>
              <Text style={styles.emptySub}>
                {recipient === 'AGENT'
                  ? 'Contact support for help with this job'
                  : `Send a message to the ${recipient.toLowerCase()}`}
              </Text>
            </View>
          )
        }
        renderItem={({ item }) => {
          const isOutbound = !item.isInbound;
          const isSystem = item.senderType === 'SYSTEM';

          if (isSystem) {
            return (
              <View style={styles.systemMsg}>
                <Text style={styles.systemMsgText}>{item.messageText}</Text>
              </View>
            );
          }

          return (
            <View style={[styles.bubbleRow, isOutbound ? styles.bubbleRowOut : styles.bubbleRowIn]}>
              <View style={[styles.bubble, isOutbound ? styles.bubbleOut : styles.bubbleIn]}>
                <Text style={[styles.bubbleText, isOutbound ? styles.bubbleTextOut : styles.bubbleTextIn]}>
                  {item.messageText}
                </Text>
                <View style={styles.bubbleFooter}>
                  <Text style={[styles.timestamp, isOutbound && styles.timestampOut]}>
                    {new Date(item.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </Text>
                  {isOutbound && (() => {
                    const pending = item.id.startsWith('m-');
                    const failed = (item.status as string) === 'FAILED' || (item.status as string) === 'undelivered';
                    const delivered = (item.status as string) === 'DELIVERED' || (item.status as string) === 'delivered';
                    const label = failed ? 'Failed' : pending ? 'Sending' : delivered ? 'Delivered' : 'Sent';
                    const color = failed ? '#FCA5A5' : delivered ? RD.money : 'rgba(255,255,255,0.6)';
                    return (
                      <View style={styles.statusRow}>
                        <Ionicons
                          name={failed ? 'alert-circle' : delivered ? 'checkmark-done' : pending ? 'time-outline' : 'checkmark'}
                          size={delivered ? 13 : 11}
                          color={color}
                        />
                        <Text style={[styles.statusText, { color }]}>{label}</Text>
                      </View>
                    );
                  })()}
                </View>
              </View>
            </View>
          );
        }}
      />

      {/* Quick Messages */}
      {showQuick && (
        <View style={styles.quickPanel}>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.quickScroll}>
            {QUICK_MESSAGES.map(qm => (
              <TouchableOpacity
                key={qm.key}
                style={styles.quickBtn}
                onPress={() => sendQuickMessage(qm.key)}
                activeOpacity={0.7}
              >
                <Ionicons name={qm.icon as any} size={14} color={RD.text} />
                <Text style={styles.quickText}>{qm.label}</Text>
              </TouchableOpacity>
            ))}
          </ScrollView>
        </View>
      )}

      {/* Input Bar */}
      <View style={styles.inputBar}>
        <TouchableOpacity
          style={[styles.quickToggle, showQuick && styles.quickToggleActive]}
          onPress={() => setShowQuick(!showQuick)}
        >
          <Ionicons name="flash" size={18} color={showQuick ? '#fff' : RD.maroon} />
        </TouchableOpacity>
        <TextInput
          style={styles.textInput}
          value={text}
          onChangeText={setText}
          placeholder={`Message ${recipientName.toLowerCase()}…`}
          placeholderTextColor={RD.g40}
          multiline
          maxLength={500}
          onSubmitEditing={send}
        />
        <TouchableOpacity
          style={[styles.sendBtn, !text.trim() && styles.sendBtnDisabled]}
          onPress={send}
          disabled={!text.trim() || sending}
        >
          <Ionicons name="arrow-up" size={19} color="#fff" />
        </TouchableOpacity>
      </View>
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  container:          { flex: 1, backgroundColor: RD.bg },

  // Header
  header:             { backgroundColor: RD.surface, paddingHorizontal: spacing.base, paddingTop: Platform.OS === 'ios' ? 56 : 36, paddingBottom: spacing.sm, flexDirection: 'row', alignItems: 'center', gap: 12, borderBottomWidth: 1, borderBottomColor: RD.border },
  backBtn:            { padding: 4, minWidth: 44, minHeight: 44, justifyContent: 'center', alignItems: 'center' },
  headerCenter:       { flex: 1 },
  headerTitle:        { fontSize: 18, fontWeight: '700', color: RD.text },
  headerSub:          { fontSize: 11, color: RD.textFaint, marginTop: 1 },

  // Recipient tabs — segmented control
  tabRow:             { flexDirection: 'row', marginHorizontal: 14, marginTop: spacing.sm, marginBottom: spacing.xs, padding: 4, gap: 4, backgroundColor: RD.g05, borderRadius: 14 },
  tab:                { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, height: 36, borderRadius: 10, backgroundColor: 'transparent' },
  tabActive:          { backgroundColor: RD.ink },
  tabText:            { fontSize: 13, fontWeight: '600', color: RD.textSec },
  tabTextActive:      { color: '#fff' },
  unreadDot:          { position: 'absolute', top: 3, right: 8, backgroundColor: RD.maroon, borderRadius: 8, minWidth: 16, height: 16, justifyContent: 'center', alignItems: 'center', paddingHorizontal: 4 },
  unreadText:         { color: '#fff', fontSize: 9.5, fontWeight: '700' },

  // Proxy notice
  proxyNotice:        { flexDirection: 'row', alignItems: 'center', gap: 7, paddingHorizontal: 18, paddingBottom: 8 },
  proxyText:          { fontSize: 11, color: RD.g40, flex: 1 },

  // Messages
  list:               { paddingHorizontal: 16, paddingTop: 6, paddingBottom: 14, gap: 8 },
  emptyState:         { alignItems: 'center', paddingTop: 80, gap: 6 },
  emptyText:          { fontSize: 16, fontWeight: '600', color: RD.textSec },
  emptySub:           { fontSize: 13, color: RD.textFaint },
  retryBtn:           { flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: spacing.md, paddingHorizontal: 16, height: 36, borderRadius: 10, backgroundColor: RD.ink },
  retryText:          { fontSize: 13, fontWeight: '600', color: '#fff' },

  // System messages
  systemMsg:          { alignSelf: 'center', backgroundColor: RD.g07, borderRadius: radii.md, paddingHorizontal: 12, paddingVertical: 6, marginVertical: 4 },
  systemMsgText:      { fontSize: 12, color: RD.textMuted, textAlign: 'center' },

  // Bubbles
  bubbleRow:          { flexDirection: 'row', alignItems: 'flex-end' },
  bubbleRowIn:        { justifyContent: 'flex-start' },
  bubbleRowOut:       { justifyContent: 'flex-end' },
  bubble:             { maxWidth: '76%', borderRadius: 20, paddingHorizontal: 14, paddingVertical: 10 },
  bubbleIn:           { backgroundColor: RD.surface, borderBottomLeftRadius: 6, borderWidth: 1, borderColor: RD.border },
  bubbleOut:          { backgroundColor: RD.ink, borderBottomRightRadius: 6 },
  bubbleText:         { fontSize: 14.5, lineHeight: 20 },
  bubbleTextIn:       { color: RD.text },
  bubbleTextOut:      { color: '#fff' },
  bubbleFooter:       { flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 5, alignSelf: 'flex-end' },
  timestamp:          { fontSize: 10.5, color: RD.g30 },
  timestampOut:       { color: 'rgba(255,255,255,0.45)' },
  statusRow:          { flexDirection: 'row', alignItems: 'center', gap: 3 },
  statusText:         { fontSize: 10.5, fontWeight: '500' },

  // Quick messages
  quickPanel:         { backgroundColor: RD.surface, borderTopWidth: 1, borderTopColor: RD.border, paddingVertical: 8 },
  quickScroll:        { paddingHorizontal: 16, gap: 7 },
  quickBtn:           { flexDirection: 'row', alignItems: 'center', gap: 5, paddingHorizontal: 12, paddingVertical: 7, borderRadius: radii.full, backgroundColor: RD.surface, borderWidth: 1, borderColor: RD.g10 },
  quickText:          { fontSize: 12, fontWeight: '600', color: RD.textSec },

  // Input bar
  inputBar:           { flexDirection: 'row', paddingHorizontal: 12, paddingTop: 10, backgroundColor: RD.surface, gap: 9, alignItems: 'center', borderTopWidth: 1, borderTopColor: RD.border, paddingBottom: Platform.OS === 'ios' ? 30 : 14 },
  quickToggle:        { width: 40, height: 40, borderRadius: 20, backgroundColor: RD.g05, justifyContent: 'center', alignItems: 'center' },
  quickToggleActive:  { backgroundColor: RD.maroon },
  textInput:          { flex: 1, backgroundColor: RD.g03, borderRadius: 21, paddingHorizontal: 16, paddingVertical: 12, fontSize: 14.5, color: RD.text, maxHeight: 100 },
  sendBtn:            { width: 42, height: 42, borderRadius: 21, backgroundColor: RD.ink, justifyContent: 'center', alignItems: 'center' },
  sendBtnDisabled:    { backgroundColor: RD.g15 },
});
